import React, { useEffect, useRef, useState } from 'react';
import { fetchConsultorResumo } from '../../../../api/contac/contacAnalyticsApi';
import {
  downloadRelatorioCsvDreConsultor,
  downloadRelatorioXlsxDreConsultor,
} from '../../../../api/contac/contacReportsConsultoresApi';
import FullScreenLoader from '../../../../components/loading/FullScreenLoader';
import ArrowDown from '../../../../img/arrow-down.svg';
import ArrowUp from '../../../../img/arrow-up.svg';
import ContacDocumentOrange from '../../../../img/contac_document_orange.svg';
import { toast } from 'react-toastify';
import '../placa-dre/ModalPlacaDre.css';

interface ModalConsultorDreProps {
  codigoVoluntario: number;
  nomeVoluntario: string;
  dataInicio: string;
  dataFim: string;
  isOpen: boolean;
  onClose: () => void;
}

type Detalhe = { mes: string; valor: number };
type ClassificacaoResp = { classificacao: string; total: number; detalhes: Detalhe[] };

const classBySign3 = (n?: number) =>
  typeof n === 'number' && n < 0 ? 'value-negative3' : 'value-positive3';

const formatCurrency = (valor: number | null | undefined) =>
  typeof valor === 'number'
    ? valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })
    : '0,00';

const formatPercent = (n: number | null | undefined) =>
  typeof n === 'number' && Number.isFinite(n)
    ? `${n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`
    : '—';

const pctOf = (signedValue: number, baseAbs: number) =>
  baseAbs === 0 ? null : (signedValue / baseAbs) * 100;

const renderValorPct = (
  displayValue: React.ReactNode,
  signedForPercent: number,
  baseAbs: number,
  percentClassName?: string
) => (
  <div className="dre-value-pct">
    {displayValue}
    <span className={`dre-percent ${percentClassName ?? ''}`}>
      {formatPercent(pctOf(signedForPercent, baseAbs))}
    </span>
  </div>
);

type LinhaCompetencia = {
  grupo: string | null;
  linha: string | null;
  competencia: string;
  total_entradas: number;
  total_saidas: number;
  classificacao: string | null;
};

const toNum = (v: any) => (typeof v === 'number' ? v : Number(v ?? 0)) || 0;

const sumGrupoTotal = (
  competencias: Record<string, LinhaCompetencia[]> | undefined,
  grupoAlvo: string,
  tipo: 'ENTRADA' | 'SAIDA',
) => {
  let total = 0;
  for (const linhas of Object.values(competencias ?? {})) {
    for (const l of linhas ?? []) {
      if (l?.grupo !== grupoAlvo) continue;
      if (l?.linha !== 'TOTAL') continue;
      total += tipo === 'ENTRADA' ? toNum(l.total_entradas) : toNum(l.total_saidas);
    }
  }
  return total;
};

const buildClassificacoes = (
  competencias: Record<string, LinhaCompetencia[]> | undefined,
  grupoAlvo: string,
  tipo: 'ENTRADA' | 'SAIDA',
) => {
  const acc: Record<string, { classificacao: string; total: number; porMes: Record<string, number> }> = {};
  for (const [mes, linhas] of Object.entries(competencias ?? {})) {
    for (const l of linhas ?? []) {
      if (l?.grupo !== grupoAlvo) continue;
      if (l?.linha !== 'CLASSIFICACAO') continue;
      const key = (l?.classificacao ?? '').trim() || 'Sem Classificação';
      const v = tipo === 'ENTRADA' ? toNum(l.total_entradas) : toNum(l.total_saidas);
      if (!acc[key]) acc[key] = { classificacao: key, total: 0, porMes: {} };
      acc[key].total += v;
      acc[key].porMes[mes] = (acc[key].porMes[mes] ?? 0) + v;
    }
  }
  return Object.values(acc)
    .map((x) => ({
      classificacao: x.classificacao,
      total: x.total,
      porMes: Object.entries(x.porMes).map(([competencia, valor]) => ({ competencia, valor })),
    }))
    .sort((a, b) => b.total - a.total);
};

const mapClassifs = (arr: any[] = []): ClassificacaoResp[] =>
  arr.map((c: any) => ({
    classificacao: c?.classificacao ?? 'Sem Classificação',
    total: toNum(c?.total),
    detalhes: (Array.isArray(c?.porMes) ? c.porMes : c?.detalhes || []).map((m: any) => ({
      mes: m?.competencia ?? m?.mes ?? '',
      valor: toNum(m?.valor),
    })),
  }));

const ModalConsultorDre: React.FC<ModalConsultorDreProps> = ({
  codigoVoluntario,
  nomeVoluntario,
  dataInicio,
  dataFim,
  isOpen,
  onClose,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  const [loading, setLoading] = useState(false);          // carrega dados do DRE (fetch)
  const [downloading, setDownloading] = useState(false);  // gera CSV
  const [expanded, setExpanded] = useState<string[]>([]);
  const [resultado, setResultado] = useState<any | null>(null);

  const toggleExpand = (key: string) =>
    setExpanded((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  const handleDownloadCsv = async () => {
    try {
      setDownloading(true);

      await downloadRelatorioCsvDreConsultor({
        dataInicio,
        dataFim,
        consultores: [String(codigoVoluntario)],
        // consultor: resultado?.consultor || nomeVoluntario, // opcional (não precisa)
      });

      toast.success('Relatório CSV gerado com sucesso!');
    } catch {
      toast.error('Erro ao gerar CSV');
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadXlsx = async () => {
    try {
      setDownloading(true);

      await downloadRelatorioXlsxDreConsultor({
        dataInicio,
        dataFim,
        consultores: [String(codigoVoluntario)],
      });

      toast.success('Relatório XLSX gerado com sucesso!');
    } catch {
      toast.error('Erro ao gerar XLSX');
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    if (!isOpen || !codigoVoluntario) return;

    let active = true;

    const run = async () => {
      try {
        setLoading(true);

        const resp: any = await fetchConsultorResumo({
          dataInicio,
          dataFim,
          codigo_consultor: String(codigoVoluntario),
        });

        if (!active) return;

        const item =
          resp?.item ??
          (Array.isArray(resp?.items) ? resp.items[0] : null) ??
          resp ??
          null;

        if (!item) {
          setResultado(null);
          return;
        }

        const i = item || {};
        const d = i.detalhamento || {};
        const competencias = (i.competencias ?? d.competencias) as
          | Record<string, LinhaCompetencia[]>
          | undefined;

        const receitaSga = toNum(d.receitaMensalidade);
        const receitaNasajon = toNum(d.receitaNasajonComPlaca);
        const rateioEntrada = toNum(d.receitaRateio);

        const saidaNasajon = toNum(d.despesaNasajonComPlaca);
        const saidaEasy = toNum(d.despesaEasy);
        const rateioSaida = toNum(d.despesaRateio) + toNum(d.despesaEasyExcedente);
        const saidaRecorrente = toNum(d.despesaRecorrente);
        const saidaRecorrenteExcedente = toNum(d.despesaRecorrenteExcedente);

        const receitaOperacionalLegacy = receitaSga + receitaNasajon;
        const outrasReceitasLegacy = rateioEntrada;

        const receitaOperacional = competencias
          ? sumGrupoTotal(competencias, 'RECEITA OPERACIONAL', 'ENTRADA')
          : receitaOperacionalLegacy;

        const outrasReceitas = competencias
          ? sumGrupoTotal(competencias, 'OUTRAS RECEITAS', 'ENTRADA')
          : outrasReceitasLegacy;

        const receitaBruta = competencias
          ? toNum(i.totaisEntrada ?? i.receitaBruta ?? (receitaOperacional + outrasReceitas))
          : receitaOperacional + outrasReceitas;

        const despesasOperacionaisLegacy = saidaNasajon + saidaEasy + saidaRecorrente + saidaRecorrenteExcedente;

        const despesasOperacionais = competencias
          ? sumGrupoTotal(competencias, 'DESPESAS OPERACIONAIS', 'SAIDA')
          : despesasOperacionaisLegacy;

        const margemContribuicao = competencias
          ? sumGrupoTotal(competencias, 'MARGEM DE CONTRIBUIÇÃO', 'SAIDA')
          : rateioSaida;

        const resultadoOperacional = receitaBruta - despesasOperacionais;
        const resultadoFinal = toNum(i.resultado ?? d.resultado ?? (resultadoOperacional - margemContribuicao));

        const entradasOp = competencias
          ? mapClassifs(buildClassificacoes(competencias, 'RECEITA OPERACIONAL', 'ENTRADA'))
          : mapClassifs(d.classificacoesEntradaPlaca ?? i.classificacoesEntradaPlaca ?? []);

        const saidasOpBase = competencias
          ? mapClassifs(buildClassificacoes(competencias, 'DESPESAS OPERACIONAIS', 'SAIDA'))
          : mapClassifs(d.classificacoesSaidaPlaca ?? i.classificacoesSaidaPlaca ?? []);

        const alreadyHasExcedente = saidasOpBase.some((x) =>
          String(x.classificacao).toLowerCase().includes('recorrente excedente'),
        );

        const saidasOp = [
          ...saidasOpBase,
          ...(saidaRecorrenteExcedente && !alreadyHasExcedente
            ? [
                {
                  classificacao: 'Despesa Recorrente Excedente',
                  total: saidaRecorrenteExcedente,
                  detalhes: [{ mes: 'Período', valor: saidaRecorrenteExcedente }],
                } as ClassificacaoResp,
              ]
            : []),
        ];

        const entradasRateio = competencias
          ? mapClassifs(buildClassificacoes(competencias, 'OUTRAS RECEITAS', 'ENTRADA'))
          : mapClassifs(d.classificacoesEntradaRateio ?? i.classificacoesEntradaRateio ?? []);

        const saidasRateio = competencias
          ? mapClassifs(buildClassificacoes(competencias, 'MARGEM DE CONTRIBUIÇÃO', 'SAIDA'))
          : mapClassifs(d.classificacoesSaidaRateio ?? i.classificacoesSaidaRateio ?? []);

        const consultorNome =
          i.consultor ??
          i.consultor_nome ??
          i.consultorName ??
          i.nome_consultor ??
          nomeVoluntario ??
          '';

        const codigo =
          i.codigoVoluntario ??
          i.codigo_consultor ??
          i.consultor_codigo ??
          i.consultorCodigo ??
          codigoVoluntario;

        setResultado({
          consultor: consultorNome,
          codigoVoluntario: String(codigo),

          receitaBruta,
          receitaOperacional,
          outrasReceitas,

          despesasOperacionais,
          margemContribuicao,

          resultadoOperacional,
          resultadoFinal,

          placasAtivas: toNum(i.placasAtivas),
          placasPeriodo: toNum(i.placasPeriodo),

          entradasClassificacoes: entradasOp,
          saidasClassificacoes: saidasOp,
          entradasClassificacoesRateio: entradasRateio,
          saidasClassificacoesRateio: saidasRateio,
        });

        setExpanded([]);
      } catch {
        if (!active) return;
        toast.error('Erro ao buscar DRE do consultor.');
        setResultado(null);
      } finally {
        if (!active) return;
        setLoading(false);
      }
    };

    run();
    return () => {
      active = false;
    };
  }, [codigoVoluntario, dataInicio, dataFim, isOpen, nomeVoluntario]);

  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) onClose();
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

    if (!isOpen) return null;

    const baseRbAbs = Math.abs(Number(resultado?.receitaBruta) || 0);

    return (
    <>
      {downloading && <FullScreenLoader />}

      <div className="status-modal-overlay">
        <div className="status-modal-dre-content" ref={modalRef} style={{ margin: '0 auto' }}>
          {/* Header + botão */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div className="modal-header">{resultado?.consultor || nomeVoluntario}</div>

            <div
              className="dre-save-btn MouseHover"
              onClick={handleDownloadCsv}
              aria-disabled={downloading}
              style={{ opacity: downloading ? 0.6 : 1, pointerEvents: downloading ? 'none' : 'auto' }}
              title="Gerar CSV do DRE do consultor"
            >
              <div className="Frame1000003987">
                <img
                  src={ContacDocumentOrange}
                  alt="Relatórios"
                  width="19.8"
                  height="19.8"
                  style={{ cursor: 'pointer' }}
                />
                <div className="ComponentName">{downloading ? 'Gerando...' : 'Gerar CSV'}</div>
              </div>
            </div>

            <div
              className="dre-save-btn MouseHover"
              onClick={handleDownloadXlsx}
              aria-disabled={downloading}
              style={{ opacity: downloading ? 0.6 : 1, pointerEvents: downloading ? 'none' : 'auto' }}
              title="Gerar XLSX do DRE do consultor"
            >
              <div className="Frame1000003987">
                <img
                  src={ContacDocumentOrange}
                  alt="Relatórios"
                  width="19.8"
                  height="19.8"
                  style={{ cursor: 'pointer' }}
                />
                <div className="ComponentName">{downloading ? 'Gerando...' : 'Gerar XLSX'}</div>
              </div>
            </div>
          </div>

          <div className="modal-body" style={loading ? { flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: '60px 0', gap: '10px' } : undefined}>
            {loading ? (
              <>
                <div className="dre-spinner" />
                <span style={{ fontSize: '14px', color: 'var(--tema-texto-2, #666)', fontWeight: '500', fontFamily: "'AmpleSoft-Regular', sans-serif" }}>Carregando...</span>
              </>
            ) : !resultado ? (
              <div className="card">
                <div className="card-entry">Nenhum dado encontrado para este consultor nesse período.</div>
              </div>
            ) : (
              <>
                {/* Card 1 - Suas Informações */}
                <div className="card card-left">
                  <div className="modal-header">Suas informações</div>
                  <div className="info-row">
                    <span className="info-label">Consultor</span>
                    <span className="info-value">{resultado.consultor}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Código</span>
                    <span className="info-value">{String(resultado.codigoVoluntario || codigoVoluntario)}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Período</span>
                    <span className="info-value">{`${dataInicio.replace(/(\d{4})-(\d{2})-(\d{2})/g, '$3-$2-$1')} a ${dataFim.replace(/(\d{4})-(\d{2})-(\d{2})/g, '$3-$2-$1')}`}</span>
                  </div>
                </div>

                {/* Card 2 - DRE */}
                <div className="card">
                  <div className="modal-header">Demonstrativo de Resultados</div>

                  <div className="row-between dre-columns-header" style={{ marginBottom: 12, borderBottom: '1px dashed var(--tema-linha, #eef2f5)', paddingBottom: 6 }}>
                    <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                      <span className="dre-col-header" style={{ fontSize: '11px', display: 'flex', alignItems: 'center' }}>
                        Placas Ativas: <strong style={{ color: 'var(--tema-texto, #1A1C1B)', marginLeft: 4 }}>{Number(resultado?.placasAtivas ?? 0)}</strong>
                      </span>
                      <span className="dre-col-header" style={{ fontSize: '11px', display: 'flex', alignItems: 'center' }}>
                        Placas do Período: <strong style={{ color: 'var(--tema-texto, #1A1C1B)', marginLeft: 4 }}>{Number(resultado?.placasPeriodo ?? 0)}</strong>
                      </span>
                    </div>
                    <div className="dre-value-pct">
                      <span className="dre-col-header" style={{ minWidth: 120, textAlign: 'right' }}>Valor</span>
                      <span className="dre-col-header" style={{ minWidth: 70, textAlign: 'right' }}>% Rec. Bruta</span>
                    </div>
                  </div>

                  <div className="row-between">
                    <div className="dre-title">
                      Receita Bruta <span className="dre-note">(Todas as Receitas)</span>
                    </div>
                    {renderValorPct(
                      <span className="entry-text value-positive3">R$ {formatCurrency(resultado?.receitaBruta)}</span>,
                      +Math.abs(Number(resultado?.receitaBruta) || 0),
                      baseRbAbs,
                      'value-positive5'
                    )}
                  </div>

                  <div
                    className="row-between clickable"
                    onClick={() => toggleExpand('op')}
                    aria-expanded={expanded.includes('op')}
                  >
                    <div className="dre-title">
                      Receita Operacional <span className="dre-note">(Receitas ligadas às placas)</span>
                      <img
                        style={{ marginLeft: 5 }}
                        src={expanded.includes('op') ? ArrowUp : ArrowDown}
                        alt="Toggle"
                        className="imgIcon arrow-toggle"
                      />
                    </div>
                    {renderValorPct(
                      <span className="entry-text value-positive3">R$ {formatCurrency(resultado?.receitaOperacional)}</span>,
                      +Math.abs(Number(resultado?.receitaOperacional) || 0),
                      baseRbAbs,
                      'value-positive5'
                    )}
                  </div>

                  {expanded.includes('op') && (
                    <div className="subclass-container">
                      {(resultado?.entradasClassificacoes || []).map((ent: ClassificacaoResp, idx: number) => (
                        <React.Fragment key={`ent-op-${idx}`}>
                          <div className="subclass-row">
                            <div className="subclass-label">
                              <span>{ent.classificacao}</span>
                              <img
                                src={expanded.includes(`ent-op-${idx}`) ? ArrowUp : ArrowDown}
                                alt="Toggle"
                                className="imgIcon arrow-toggle clickable"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleExpand(`ent-op-${idx}`);
                                }}
                              />
                            </div>
                            <div className="subclass-value-pct">
                              <span className="subclass-value value-positive4">R$ {formatCurrency(ent.total)}</span>
                              <span className="dre-percent" style={{ visibility: 'hidden' }}>—</span>
                            </div>
                          </div>

                          {expanded.includes(`ent-op-${idx}`) &&
                            (ent.detalhes || []).map((det, dIdx) => (
                              <div className="detail-row" key={`ent-op-det-${idx}-${dIdx}`}>
                                <span className="detail-label">{det.mes}</span>
                                <div className="subclass-value-pct">
                                  <span className="detail-value value-positive4">R$ {formatCurrency(det.valor)}</span>
                                  <span className="dre-percent" style={{ visibility: 'hidden' }}>—</span>
                                </div>
                              </div>
                            ))}
                        </React.Fragment>
                      ))}
                    </div>
                  )}

                  <div className="row-between">
                    <div className="dre-title">
                      Outras Receitas <span className="dre-note">(Receitas não ligadas às placas)</span>
                    </div>
                    {renderValorPct(
                      <span className="entry-text value-positive3">R$ {formatCurrency(resultado?.outrasReceitas)}</span>,
                      +Math.abs(Number(resultado?.outrasReceitas) || 0),
                      baseRbAbs,
                      'value-positive5'
                    )}
                  </div>

                  <div
                    className="row-between clickable"
                    onClick={() => toggleExpand('co')}
                    aria-expanded={expanded.includes('co')}
                  >
                    <div className="dre-title">
                      Despesas Operacionais <span className="dre-note">(Despesas ligadas às placas)</span>
                      <img
                        style={{ marginLeft: 5 }}
                        src={expanded.includes('co') ? ArrowUp : ArrowDown}
                        alt="Toggle"
                        className="imgIcon arrow-toggle"
                      />
                    </div>
                    {renderValorPct(
                      <span className="entry-text value-negative3">
                        R$ -{formatCurrency(resultado?.despesasOperacionais)}
                      </span>,
                      -Math.abs(Number(resultado?.despesasOperacionais) || 0),
                      baseRbAbs,
                      'value-negative5'
                    )}
                  </div>

                  {expanded.includes('co') && (
                    <div className="subclass-container">
                      {(resultado?.saidasClassificacoes || []).map((sai: ClassificacaoResp, idx: number) => (
                        <React.Fragment key={`sai-dir-${idx}`}>
                          <div className="subclass-row">
                            <div className="subclass-label">
                              <span>{sai.classificacao}</span>
                              <img
                                src={expanded.includes(`sai-dir-${idx}`) ? ArrowUp : ArrowDown}
                                alt="Toggle"
                                className="imgIcon arrow-toggle clickable"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleExpand(`sai-dir-${idx}`);
                                }}
                              />
                            </div>
                            <div className="subclass-value-pct">
                              <span className="subclass-value value-negative4">R$ {formatCurrency(sai.total)}</span>
                              <span className="dre-percent" style={{ visibility: 'hidden' }}>—</span>
                            </div>
                          </div>

                          {expanded.includes(`sai-dir-${idx}`) &&
                            (sai.detalhes || []).map((det, dIdx) => (
                              <div className="detail-row" key={`sai-dir-det-${idx}-${dIdx}`}>
                                <span className="detail-label">{det.mes}</span>
                                <div className="subclass-value-pct">
                                  <span className="detail-value value-negative4">R$ {formatCurrency(det.valor)}</span>
                                  <span className="dre-percent" style={{ visibility: 'hidden' }}>—</span>
                                </div>
                              </div>
                            ))}
                        </React.Fragment>
                      ))}
                    </div>
                  )}

                  <div className="row-between">
                    <div className="dre-title">
                      Resultado Operacional <span className="dre-note">(Receita Bruta - Desp. Operacional)</span>
                    </div>
                    {renderValorPct(
                      <span className={`entry-text ${classBySign3(resultado?.resultadoOperacional)}`}>
                        R$ {formatCurrency(resultado?.resultadoOperacional)}
                      </span>,
                      Number(resultado?.resultadoOperacional) || 0,
                      baseRbAbs,
                      (Number(resultado?.resultadoOperacional) || 0) < 0 ? 'value-negative5' : 'value-positive5'
                    )}
                  </div>

                  <div className="row-between">
                    <div className="dre-title">
                      Margem de Contribuição <span className="dre-note">(Rateio de despesas não ligadas às placas)</span>
                    </div>
                    {renderValorPct(
                      <span className="entry-text value-negative3">
                        R$ -{formatCurrency(resultado?.margemContribuicao)}
                      </span>,
                      -Math.abs(Number(resultado?.margemContribuicao) || 0),
                      baseRbAbs,
                      'value-negative5'
                    )}
                  </div>

                  <div className="row-between">
                    <div className="dre-title">
                      Resultado <span className="dre-note">(Resultado Operac. - Margem)</span>
                    </div>
                    {renderValorPct(
                      <span className={`entry-text ${classBySign3(resultado?.resultadoFinal)}`}>
                        R$ {formatCurrency(resultado?.resultadoFinal)}
                      </span>,
                      Number(resultado?.resultadoFinal) || 0,
                      baseRbAbs,
                      (Number(resultado?.resultadoFinal) || 0) < 0 ? 'value-negative5' : 'value-positive5'
                    )}
                  </div>

                  <div className="modal-footer">
                    <button className="modal-close-btn" onClick={onClose}>
                      Fechar
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ModalConsultorDre;
